import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { createCD, getCDs, getCD, getResearcherProfile, saveResearcherProfile } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { capitalizeWords, formatContent } from '../utils/cdUtils';
import ResourceLimitModal from '../components/subscription/ResourceLimitModal';
import { validateWebsiteUrl } from '../utils/validation';
import '../index.css';

const CreateCDPage = () => {
  const [formData, setFormData] = useState({
    company_name: '',
    company_website: '',
    stakeholders: ''
  });
  
  // NEW: Context mode state
  const [contextMode, setContextMode] = useState('none'); // 'profile', 'custom', or 'none' - default to none
  const [customContext, setCustomContext] = useState({
    researcher_company_name: '',
    researcher_company_website: '',
    researcher_business_description: ''
  });
  const [targetOffering, setTargetOffering] = useState('');
  const [customSourceType, setCustomSourceType] = useState('custom'); // 'profile' or 'custom' - default to 'custom'
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showResourceModal, setShowResourceModal] = useState(false);
  const [resourceLimitMessage, setResourceLimitMessage] = useState('');
  const [showProfileWarning, setShowProfileWarning] = useState(false);
  const [pollingNotice, setPollingNotice] = useState('');

  const [customWebsiteError, setCustomWebsiteError] = useState('');
  const [prospectWebsiteError, setProspectWebsiteError] = useState('');
  const navigate = useNavigate();
  const { isAuthenticated, logout, user } = useAuth();
  const resultRef = useRef(null);
  
  // Styles
  const formContainerWrapperStyle = {
    background: 'white',
    borderRadius: '0.75rem',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.3), 0 10px 10px -5px rgba(0, 0, 0, 0.2)',
    overflow: 'hidden',
    margin: '0 auto 3rem auto',
    maxWidth: '56rem',
    padding: '0',
  };

  const formContainerStyle = {
    background: 'linear-gradient(to right, rgba(20, 32, 57, 0.9), rgba(30, 41, 59, 0.8))',
    borderRadius: '0.75rem',
    overflow: 'hidden',
    height: '100%',
    width: '100%'
  };

  const resultContainerWrapperStyle = {
    background: 'white',
    borderRadius: '0.75rem',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.3), 0 10px 10px -5px rgba(0, 0, 0, 0.2)',
    overflow: 'hidden',
    margin: '3rem auto',
    maxWidth: '56rem',
    padding: '0'
  };

  const resultContainerStyle = {
    background: 'linear-gradient(to right, rgba(20, 32, 57, 0.9), rgba(30, 41, 59, 0.8))',
    borderRadius: '0.75rem',
    overflow: 'hidden',
    height: '100%',
    width: '100%'
  };

  const companyHeaderStyle = {
    background: 'rgb(20, 32, 57)',
    padding: '1.5rem',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
  };

  // Check authentication
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);
  
  // Pre-fill custom context with profile data on mount if available
  useEffect(() => {
    const loadProfile = async () => {
      try {
        const profile = await getResearcherProfile();
        if (profile.company_name && profile.company_website) {
          setCustomContext({
            researcher_company_name: profile.company_name || '',
            researcher_company_website: profile.company_website || '',
            researcher_business_description: profile.business_description || ''
          });
        }
      } catch (error) {
        console.error('Error loading profile for pre-fill:', error);
      }
    };
    
    if (isAuthenticated) {
      loadProfile();
    }
  }, [isAuthenticated]);
  
  // Scroll to result when available
  useEffect(() => {
    if (result && resultRef.current) {
      resultRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [result]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    // Clear prospect website error when user types
    if (name === 'company_website') {
      setProspectWebsiteError('');
    }
  };

  const handleCustomWebsiteBlur = () => {
    const validation = validateWebsiteUrl(customContext.researcher_company_website);
    if (!validation.isValid) {
      setCustomWebsiteError(validation.error);
    } else {
      setCustomWebsiteError('');
      // Auto-normalize the URL
      if (validation.normalized !== customContext.researcher_company_website) {
        setCustomContext(prev => ({
          ...prev,
          researcher_company_website: validation.normalized
        }));
      }
    }
  };

  const handleProspectWebsiteBlur = () => {
    const validation = validateWebsiteUrl(formData.company_website);
    if (!validation.isValid) {
      setProspectWebsiteError(validation.error);
    } else {
      setProspectWebsiteError('');
      // Auto-normalize the URL
      if (validation.normalized !== formData.company_website) {
        setFormData(prev => ({
          ...prev,
          company_website: validation.normalized
        }));
      }
    }
  };

  const handleCustomContextChange = (e) => {
    const { name, value } = e.target;
    setCustomContext(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user types
    if (name === 'researcher_company_website') {
      setCustomWebsiteError('');
    }
  };

  // If the create request is cut off by a gateway/timeout, the backend is still
  // generating the report. Poll the user's report list until the new one appears
  // (an id greater than what existed before submit, matching the company), fetch
  // it in full, and render it — so the user never sees a false failure.
  const pollForResult = async (baselineMaxId, companyName) => {
    setPollingNotice("Still researching — this one's taking a little longer. Your report will appear here automatically; no need to resubmit.");
    const norm = (s) => (s || '').trim().toLowerCase();
    const deadlineMs = Date.now() + 4 * 60 * 1000; // poll up to ~4 minutes
    while (Date.now() < deadlineMs) {
      await new Promise((resolve) => setTimeout(resolve, 8000));
      try {
        const list = await getCDs();
        const match = (Array.isArray(list) ? list : [])
          .filter((c) => c && c.id > baselineMaxId && (!companyName || norm(c.company_name) === norm(companyName)))
          .sort((a, b) => b.id - a.id)[0];
        if (match) {
          const full = await getCD(match.id);
          setResult(full);
          setPollingNotice('');
          return true;
        }
      } catch (pollErr) {
        // Transient poll failure (e.g. a blip) — keep trying until the deadline.
        console.warn('Polling for generated report…', pollErr);
      }
    }
    setPollingNotice('');
    setError('Your report is taking longer than usual. It may still be generating — please check "View Past Research" in a minute.');
    return false;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setPollingNotice('');
    setShowProfileWarning(false);
    // Validate prospect website format
      const prospectWebsiteValidation = validateWebsiteUrl(formData.company_website);
      if (!prospectWebsiteValidation.isValid) {
        setProspectWebsiteError(prospectWebsiteValidation.error);
        setError('Please fix the prospect website URL format before submitting.');
        setIsLoading(false);
        return;
      }

      // Snapshot the newest existing report id BEFORE submitting, so that if this
      // request is cut off by a gateway/timeout we can detect the newly generated
      // report while polling (see pollForResult).
      let baselineMaxId = 0;

      try {
        // Determine if custom context is actually being used
        let effectiveUseContext = contextMode !== 'none';
        // Validation: if target offering is filled, require researcher context
        const hasTargetOffering = targetOffering.trim() !== '';
        if (hasTargetOffering && contextMode === 'none') {
          setError('Target offering requires company name and website to be provided');
          setIsLoading(false);
          return;
        }

        // Validation for custom context mode
        if (contextMode === 'custom') {
          const hasName = customContext.researcher_company_name.trim() !== '';
          const hasWebsite = customContext.researcher_company_website.trim() !== '';
          const hasDescription = customContext.researcher_business_description.trim() !== '';
          
          // If target offering is filled, require name and website
          if (hasTargetOffering && (!hasName || !hasWebsite)) {
            setError('Target offering requires company name and website to be provided');
            setIsLoading(false);
            return;
          }
          
          // If business description is filled, require name and website
          if (hasDescription && (!hasName || !hasWebsite)) {
            setError('Company name and website are required when providing business description');
            setIsLoading(false);
            return;
          }
          
          // If both are empty, treat as no context (generic mode)
          if (!hasName && !hasWebsite) {
            effectiveUseContext = false;
          }
          // If only one is filled, require both
          else if (hasName !== hasWebsite) {
            setError('Both company name and website must be provided, or leave both empty for generic mode.');
            setIsLoading(false);
            return;
          }
          // If both filled, validate website format
          else if (hasWebsite) {
            const validation = validateWebsiteUrl(customContext.researcher_company_website);
            if (!validation.isValid) {
              setCustomWebsiteError(validation.error);
              setError('Please fix the website URL format before submitting.');
              setIsLoading(false);
              return;
            }
          }
        }
        // Validation for custom context mode
        if (contextMode === 'custom') {
          const hasName = customContext.researcher_company_name.trim() !== '';
          const hasWebsite = customContext.researcher_company_website.trim() !== '';
          
          // If both are empty, treat as no context (generic mode)
          if (!hasName && !hasWebsite) {
            effectiveUseContext = false;
          }
          // If only one is filled, require both
          else if (hasName !== hasWebsite) {
            setError('Both company name and website must be provided, or leave both empty for generic mode.');
            setIsLoading(false);
            return;
          }
          // If both filled, validate website format
          else if (hasWebsite) {
            const validation = validateWebsiteUrl(customContext.researcher_company_website);
            if (!validation.isValid) {
              setCustomWebsiteError(validation.error);
              setError('Please fix the website URL format before submitting.');
              setIsLoading(false);
              return;
            }
          }
        }
      
      // Save to profile if using "Use my profile" mode AND fields are not empty
      if (contextMode === 'custom' && customSourceType === 'profile' && effectiveUseContext) {
        try {
          await saveResearcherProfile({
            company_name: customContext.researcher_company_name,
            company_website: customContext.researcher_company_website,
            business_description: customContext.researcher_business_description
          });
          console.log('Profile saved successfully');
        } catch (profileError) {
          console.error('Error saving profile:', profileError);
          // Don't block CD creation if profile save fails
        }
      }
      
      // Build request payload
      const payload = {
        company_name: formData.company_name,
        company_website: formData.company_website,
        stakeholders: formData.stakeholders,
        use_researcher_context: effectiveUseContext,
        target_offering: effectiveUseContext && targetOffering.trim() ? targetOffering.trim() : null
      };
      
      // Add custom context if in custom mode AND fields are not empty
      if (contextMode === 'custom' && effectiveUseContext) {
        payload.researcher_company_name = customContext.researcher_company_name;
        payload.researcher_company_website = customContext.researcher_company_website;
        payload.researcher_business_description = customContext.researcher_business_description;
      }
      
      try {
        const existing = await getCDs();
        baselineMaxId = (Array.isArray(existing) ? existing : []).reduce((m, c) => Math.max(m, c.id || 0), 0);
      } catch (baselineErr) {
        console.warn('Could not snapshot existing reports before submit', baselineErr);
      }

      const response = await createCD(payload);
      setResult(response);
    } catch (err) {
      console.error('CD creation error:', err);

      // Request cut off by a gateway/timeout — generation continues server-side.
      // Keep the loader running and poll for the finished report instead of
      // showing a false failure. (Handled before generic errors below.)
      if (err.isTimeout) {
        await pollForResult(baselineMaxId, formData.company_name);
      }
      // Handle quota exceeded error
      else if (err.isQuotaExceeded) {
        setResourceLimitMessage(err.message || 'You have reached your report generation limit for this billing period.');
        setShowResourceModal(true);
      } 
      // Handle profile required error
      else if (err.isProfileRequired) {
        setShowProfileWarning(true);
        setError(err.message);
      }
      // Handle authentication errors
      else if (err.message && err.message.includes('Authentication expired')) {
        logout();
        navigate('/login');
      }
      // Handle other errors
      else {
        setError(err.message || "An error occurred. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewAll = () => {
    navigate('/view-cds');
  };

  const handleGoToSettings = () => {
    navigate('/profile-settings');
  };

  const renderResult = () => {
    if (!result) return null;

    return (
      <div ref={resultRef} style={resultContainerWrapperStyle}>
        <div style={resultContainerStyle}>
          <div style={companyHeaderStyle}>
            <h1 className="text-3xl font-bold text-orange-500 mb-1 font-playfair">{capitalizeWords(formData.company_name)}</h1>
            <p className="text-white/80 text-sm">
              Research completed on {new Date().toLocaleDateString()}
            </p>
          </div>
          
          <div className="p-8">
            <table className="w-full border-collapse" style={{tableLayout: 'fixed', wordWrap: 'break-word'}}>
              <tbody>
                {formatContent(result.response)}
              </tbody>
            </table>
          </div>
          
          {result.additional_resources && !result.response.includes('### Citations') && (
            <div className="px-8 pb-8">
              <div className="bg-[#151f32] px-5 py-4 rounded-lg shadow-md mb-6 border border-[#1a2639]">
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-orange-500 font-bold text-xl font-playfair">
                  Additional Resources
                </span>
              </div>
              <ol className="list-decimal pl-8 mt-2 space-y-3 text-white">
                {JSON.parse(result.additional_resources).slice(0, 10).map((resource, index) => (
                  <li key={index} className="mb-2 hover:text-orange-300 transition-colors duration-200">
                    <a 
                      href={resource.link} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="text-orange-400 hover:text-orange-300 break-words underline underline-offset-2 decoration-1 hover:decoration-2 transition-all duration-200"
                    >
                      {resource.title}
                    </a>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-royal-900 to-royal-800 py-12 px-4 sm:px-6 lg:px-8 font-poppins text-white">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4 font-playfair">
            <span className="bg-gradient-to-r from-orange-400 to-orange-500 bg-clip-text text-transparent">
              Contextual Dip Stick
            </span>
          </h1>
          <div className="w-24 h-1 bg-gradient-to-r from-orange-400 to-orange-500 mx-auto mb-6"></div>
          <p className="text-royal-100 max-w-2xl mx-auto">
            Get comprehensive insights about any company with our AI-powered research tool. 
            Enter the company details below to generate your report.
          </p>
        </div>

        <div style={formContainerWrapperStyle}>
          <div style={formContainerStyle}>
            <div className="px-8 pt-8 pb-2">
              <h2 className="text-2xl font-bold text-white mb-6 font-playfair">Create New Research</h2>
            </div>
            
            <form onSubmit={handleSubmit} className="px-8 pb-8">
              {/* Prospect Company Fields */}
              <div className="space-y-6 mb-8">
                <div>
                  <label className="block text-white text-sm font-medium mb-2" htmlFor="company_name">
                    Prospect Company Name <span className="text-orange-300">*</span>
                  </label>
                  <input
                    type="text"
                    name="company_name"
                    id="company_name"
                    value={formData.company_name}
                    onChange={handleChange}
                    required
                    maxLength="50"
                    className="w-full px-4 py-3 rounded-lg bg-white border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 text-gray-900 placeholder-gray-500"
                    placeholder="Enter company name"
                  />
                </div>
                
                <div>
                  <label className="block text-white text-sm font-medium mb-2" htmlFor="company_website">
                    Prospect Company Website <span className="text-orange-300">*</span>
                  </label>
                  <input
                    type="text"
                    name="company_website"
                    id="company_website"
                    value={formData.company_website}
                    onChange={handleChange}
                    onBlur={handleProspectWebsiteBlur}
                    required
                    maxLength="50"
                    className={`w-full px-4 py-3 rounded-lg bg-white border focus:outline-none focus:ring-2 text-gray-900 placeholder-gray-500 ${
                      prospectWebsiteError ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-orange-500'
                    }`}
                    placeholder="e.g., company.com"
                  />
                  {prospectWebsiteError && (
                    <p className="mt-2 text-sm text-red-400">{prospectWebsiteError}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-white text-sm font-medium mb-2" htmlFor="stakeholders">
                    Leadership Names
                  </label>
                  <input
                    type="text"
                    name="stakeholders"
                    id="stakeholders"
                    value={formData.stakeholders}
                    onChange={handleChange}
                    maxLength="50"
                    className="w-full px-4 py-3 rounded-lg bg-white border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 text-gray-900 placeholder-gray-500"
                    placeholder="e.g., John Smith, Jane Doe"
                  />
                  <p className="mt-2 text-sm text-white/70">
                    Enter names of key leadership (Optional, default: Top leadership)
                  </p>
                </div>
              </div>
              {/* Compact Context Mode - Single Row */}
              <div className="mb-6 flex items-center space-x-4">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={contextMode === 'custom'}
                    onChange={(e) => {
                      setContextMode(e.target.checked ? 'custom' : 'none');
                    }}
                    className="w-4 h-4 text-orange-500 border-gray-300 rounded focus:ring-orange-500"
                  />
                  <span className="ml-2 text-sm text-white">Use custom context for this search</span>
                </label>
              </div>

              {/* Custom Context Fields - Compact */}
              {contextMode === 'custom' && (
                <div className="mb-6 p-4 bg-white/5 rounded-lg border border-white/10 space-y-3">
                  <div className="flex items-center space-x-4 mb-3">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="customSource"
                        value="profile"
                        checked={customSourceType === 'profile'}
                        onChange={() => {
                          setCustomSourceType('profile');
                          // Load profile data into custom fields
                          getResearcherProfile().then(profile => {
                            setCustomContext({
                              researcher_company_name: profile.company_name || '',
                              researcher_company_website: profile.company_website || '',
                              researcher_business_description: profile.business_description || ''
                            });
                          }).catch(error => {
                            console.log('No profile found, starting with empty fields');
                            // Keep empty fields if no profile exists
                          });
                        }}
                        className="w-3 h-3 text-orange-500 focus:ring-orange-500"
                      />
                      <span className="ml-2 text-xs text-white">Use my profile</span>
                    </label>
                    
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="customSource"
                        value="custom"
                        checked={customSourceType === 'custom'}
                        onChange={() => {
                          setCustomSourceType('custom');
                        }}
                        className="w-3 h-3 text-orange-500 focus:ring-orange-500"
                      />
                      <span className="ml-2 text-xs text-white">Enter custom (one-time use)</span>
                    </label>
                  </div>
                  
                  {/* Helper text for "Use my profile" mode */}
                  {customSourceType === 'profile' && (
                    <div className="mb-3 p-2 bg-blue-500/10 border border-blue-500/30 rounded text-xs text-blue-300">
                      💡 Changes will be saved to your profile for future use
                    </div>
                  )}

                  <div>
                    <label className="block text-white text-xs font-medium mb-1" htmlFor="custom_company_name">
                      Your Company Name
                    </label>
                    <input
                      type="text"
                      name="researcher_company_name"
                      id="custom_company_name"
                      value={customContext.researcher_company_name}
                      onChange={handleCustomContextChange}
                      maxLength="200"
                      className="w-full px-3 py-2 text-sm rounded-lg bg-white border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 text-gray-900 placeholder-gray-500"
                      placeholder="e.g., Acme Corp"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-white text-xs font-medium mb-1" htmlFor="custom_company_website">
                      Your Company Website
                    </label>
                    <input
                      type="text"
                      name="researcher_company_website"
                      id="custom_company_website"
                      value={customContext.researcher_company_website}
                      onChange={handleCustomContextChange}
                      onBlur={handleCustomWebsiteBlur}
                      maxLength="100"
                      className={`w-full px-3 py-2 text-sm rounded-lg bg-white border focus:outline-none focus:ring-2 text-gray-900 placeholder-gray-500 ${
                        customWebsiteError ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-orange-500'
                      }`}
                      placeholder="e.g., acmecorp.com"
                    />
                    {customWebsiteError && (
                      <p className="mt-1 text-xs text-red-400">{customWebsiteError}</p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-white text-xs font-medium mb-1" htmlFor="custom_business_description">
                      What You Offer
                    </label>
                    <input
                      type="text"
                      name="researcher_business_description"
                      id="custom_business_description"
                      value={customContext.researcher_business_description}
                      onChange={handleCustomContextChange}
                      maxLength="100"
                      className="w-full px-3 py-2 text-sm rounded-lg bg-white border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 text-gray-900 placeholder-gray-500"
                      placeholder="e.g., Cloud migration services"
                    />
                  </div>
                  
                  {customSourceType === 'custom' && (
                    <div>
                      <label className="block text-white text-xs font-medium mb-1" htmlFor="target_offering">
                        Target Offering (Optional)
                      </label>
                      <input
                        type="text"
                        name="target_offering"
                        id="target_offering"
                        value={targetOffering}
                        onChange={(e) => setTargetOffering(e.target.value)}
                        maxLength="100"
                        className="w-full px-3 py-2 text-sm rounded-lg bg-white border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 text-gray-900 placeholder-gray-500"
                        placeholder="e.g., AWS implementation, CRM software"
                      />
                      <p className="mt-1 text-xs text-white/70">
                        Specify what you're selling to know stakeholder's targeted objections
                      </p>
                    </div>
                  )}
                </div>
              )}
              {/* Profile Warning */}
              {showProfileWarning && (
                <div className="mb-6 bg-orange-500/20 border-l-4 border-orange-500 p-4 rounded-lg">
                  <div className="flex items-start">
                    <svg className="h-6 w-6 text-orange-400 mr-3 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <div>
                      <h3 className="text-lg font-medium text-orange-300 mb-1">Profile Required</h3>
                      <p className="text-white mb-3">{error}</p>
                      <button
                        type="button"
                        onClick={handleGoToSettings}
                        className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-lg transition-colors"
                      >
                        Go to Settings
                      </button>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="flex flex-col items-center space-y-4">
                <button 
                  type="submit" 
                  className="w-64 px-4 py-3 bg-gradient-to-r from-orange-500 to-amber-600 text-white font-semibold rounded-lg shadow-md hover:from-orange-600 hover:to-amber-700 focus:outline-none focus:ring-2 focus:ring-orange-500 transition-all duration-200 flex items-center justify-center"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Processing...
                    </>
                  ) : 'Generate Research'}
                </button>
                
                <button 
                  onClick={handleViewAll} 
                  className="w-64 px-4 py-3 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-lg border border-white/20 focus:outline-none focus:ring-2 focus:ring-white/50 transition-all duration-200"
                  type="button"
                  disabled={isLoading}
                >
                  View Past Research
                </button>
              </div>
            </form>
          </div>
        </div>

        {isLoading && (
          <div className="my-12 flex flex-col items-center justify-center">
            <div className="relative w-24 h-24">
              <div className="absolute top-0 left-0 w-full h-full border-8 border-white/20 rounded-full"></div>
              <div className="absolute top-0 left-0 w-full h-full border-8 border-transparent border-t-orange-400 rounded-full animate-spin"></div>
            </div>
            <p className="mt-6 text-xl font-medium text-white">Digging Deep...</p>
            <p className="mt-2 text-white/70">{pollingNotice || 'Our AI is gathering comprehensive insights. This may take a moment.'}</p>
          </div>
        )}

        {error && !showProfileWarning && (
          <div className="mt-8 bg-red-500/20 border-l-4 border-red-500 p-6 rounded-lg">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-red-300">Error</h3>
                <p className="mt-1 text-white">{error}</p>
                <p className="mt-3 text-sm text-red-300">Please try again or contact support if the issue persists.</p>
              </div>
            </div>
          </div>
        )}

        {renderResult()}

        {showResourceModal && (
          <ResourceLimitModal
            user={user}
            message={resourceLimitMessage || "You have reached your AI generation limit for this billing period.Upgrade your plan to continue creating competitive intelligence reports."}
            resourceType="requests"
            onClose={() => setShowResourceModal(false)}
            onUpgrade={() => {
              setShowResourceModal(false);
              navigate('/subscription-dashboard');
            }}
          />
        )}
      </div>
    </div>
  );
};

export default CreateCDPage;